<script lang="ts" setup>
import { ref, onMounted, defineAsyncComponent, computed } from "vue";
import * as XLSX from 'xlsx';
import { fetchGetApi, fetchDeleteApi } from '@/util/api';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';
import Swal from "sweetalert2";

interface PastAdvanceRequest {
    _id: string;
    advanceAmount: number;
    advanceDate: string;
    advanceStatus: string;
    advanceReason: string;
    createdAt: string;
    updatedAt: string;
}

interface AdvanceSalaryRequest {
    _id: string;
    logNo: number;
    date: string;
    employeeName: string;
    empId: string;
    site: string;
    dept: string;
    designation: string;
    daysWorked: string;
    amount: number;
    reason: string;
    guarantor: string;
    guarantorId: string | null;
    guarantorEmpCode: string | null;
    isGuarantorAccepted: boolean;
    advanceFormUrl: string | null;
    autoCheckStatus: string;
    criticalReq: boolean;
    managerName: string;
    pastAdvances6M: string;
    pastAdvanceRequests: PastAdvanceRequest[];
    status: string;
    createdAt: string;
    updatedAt: string;
}

const baseUrl = process.env.VUE_APP_BASE_URL;
const allData = ref<AdvanceSalaryRequest[]>([]);
const elementsPerPage = ref(10); // Changed to 10 for better UX
const currentPage = ref(1);
const filterQuery = ref("");
const toast = useToast();
const isLoading = ref(false);
const showModal = ref(false);
const selectedItem = ref<AdvanceSalaryRequest | null>(null);
const showImageModal = ref(false);
const selectedImageUrl = ref<string | null>(null);
const showPastAdvancesModal = ref(false);
const selectedPastAdvances = ref<any[]>([]);

const ModalCode = defineAsyncComponent(() => import("@/pages/operation/modal/editAdvanceSalary.vue"));

const getAllData = async () => {
    isLoading.value = true;
    try {
        const response = await fetchGetApi(`api/advance/salary/get/all`);
        allData.value = response.data.data;
    } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch data. Please try again later.");
    } finally {
        isLoading.value = false;
    }
}

onMounted(async () => {
    await getAllData();
});

const downloadExcel = () => {
    try {
        toast.info('Downloading Excel...');
        const worksheet = XLSX.utils.json_to_sheet(allData.value);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Advance Salary Requests");
        XLSX.writeFile(workbook, 'advance_salary_requests.xlsx');
        toast.success('Excel file downloaded successfully');
    } catch (error) {
        toast.error('Failed to download Excel file');
    }
}

const filteredData = computed(() => {
    const query = filterQuery.value.toLowerCase();
    return allData.value.filter(item =>
        item.employeeName.toLowerCase().includes(query) ||
        item.empId.toLowerCase().includes(query) ||
        item.site.toLowerCase().includes(query) ||
        item.reason.toLowerCase().includes(query) ||
        item.status.toLowerCase().includes(query)
    );
});

const num_pages = computed(() => {
    return Math.ceil(filteredData.value.length / elementsPerPage.value);
});

const change_page = (page: number) => {
    currentPage.value = page;
}

const previousPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
}

const nextPage = () => {
    if (currentPage.value < num_pages.value) {
        currentPage.value++;
    }
}

const paginatedData = computed(() => {
    const start = (currentPage.value - 1) * elementsPerPage.value;
    const end = start + elementsPerPage.value;
    return filteredData.value.slice(start, end);
});

const approveRequest = async (item: AdvanceSalaryRequest) => {
    try {
        toast.info('Approving request...');
        await fetchGetApi(`api/advance/salary/approve/${item._id}`);
        await getAllData();
        toast.success('Request approved successfully');

    } catch (error) {
        console.error('Error approving request:', error);
        toast.error('Failed to approve request');
    }
}

const deleteRequest = async (item: AdvanceSalaryRequest) => {
    try {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            toast.info('Deleting request...');
            await fetchDeleteApi(`api/advance/salary/delete/${item._id}`);
            await getAllData();
            toast.success('Request deleted successfully');
        }
    } catch (error) {
        toast.error('Failed to delete request');
    }
}

const rejectRequest = async (item: AdvanceSalaryRequest) => {
    try {
        toast.info('Rejecting request...');
        await fetchGetApi(`api/advance/salary/reject/${item._id}`);
        await getAllData();
        toast.success('Request rejected successfully');
    } catch (error) {
        toast.error('Failed to reject request');
    }
}

const modifyRequest = async (item: AdvanceSalaryRequest) => {
    selectedItem.value = item;
    showModal.value = true;
}

const handleAction = async (action: string, item: AdvanceSalaryRequest) => {
    try {
        // Implement action handlers
        switch(action) {
            case 'approve':
                await approveRequest(item);
                break;
            case 'reject':
                await rejectRequest(item);
                break;
            case 'modify':
                await modifyRequest(item);
                break;
            case 'delete':
                await deleteRequest(item);
                break;
        }
    } catch (error) {
        toast.error('Action failed. Please try again.');
    }
}

const viewImage = (url: string) => {
    selectedImageUrl.value = url;
    showImageModal.value = true;
}

const closeImageModal = () => {
    showImageModal.value = false;
    selectedImageUrl.value = null;
}

const viewPastAdvances = (pastAdvances: any[]) => {
    selectedPastAdvances.value = pastAdvances;
    showPastAdvancesModal.value = true;
}

const closePastAdvancesModal = () => {
    showPastAdvancesModal.value = false;
    selectedPastAdvances.value = [];
}
</script>

<template>
    <Breadcrumbs main="Apps" title="Advance Salary Request" />
    <div class="container-fluid">
        <div class="email-wrap bookmark-wrap">
            <div class="row">
                <div class="col-12">
                    <div class="card">
                      
                        <div class="card-body x">
                            <div class="product-table">
                                <div class="mb-3 row justify-content-between align-items-center">
                                    <div class="col-sm-6 col-md-4">
                                        <div class="search-box">
                                            <label for="table-complete-search" class="col-form-label">Search:</label>
                                            <input 
                                                id="table-complete-search" 
                                                type="text" 
                                                class="form-control" 
                                                v-model="filterQuery" 
                                                placeholder="Search by Employee, Emp ID, Site, Reason, or Status"
                                            >
                                        </div>
                                    </div>
                                    <div class="col-sm-auto d-flex gap-2">
                                        <router-link to="/reports/advance-salary-reports" class="btn btn-info">
                                            <i class="fa fa-file-text me-2"></i>Reports
                                        </router-link>
                                        <button type="button" class="btn btn-primary" @click="downloadExcel">
                                            <i class="fa fa-download me-2"></i>Download Excel
                                        </button>
                                    </div>
                                </div>

                                <div v-if="isLoading" class="text-center py-4">
                                    <div class="spinner-border text-primary" role="status">
                                        <span class="visually-hidden">Loading...</span>
                                    </div>
                                </div>

                                <table v-else class="table table-bordered table-hover">
                                    <thead>
                                        <tr>
                                            <th scope="col" style="width: 50px;">Log#</th>
                                            <th scope="col">Employee Name</th>
                                            <th scope="col">Emp ID</th>
                                            <th scope="col">Site</th>
                                            <th scope="col">Amount</th>
                                            <th scope="col">Date</th>
                                            <th scope="col">Reason</th>
                                            <th scope="col">Guarantor</th>
                                            <th scope="col">Guarantor Status</th>
                                            <th scope="col">Auto Check</th>
                                            <th scope="col">Critical</th>
                                            <th scope="col">Status</th>
                                            <th scope="col">Past Advance Track</th>
                                               <th scope="col">Provided Image</th>
                                            <th scope="col" style="width: 150px;">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="item in paginatedData" :key="item._id">
                                            <td>{{ item.logNo }}</td>
                                            <td>{{ item.employeeName }}</td>
                                            <td>{{ item.empId }}</td>
                                            <td>{{ item.site }}</td>
                                            <td>₹{{ item.amount.toLocaleString() }}</td>
                                            <td>{{ new Date(item.date).toLocaleDateString() }}</td>
                                            <td>{{ item.reason }}</td>
                                            <td>
                                                <div v-if="item.guarantor && item.guarantor !== 'N/A'">
                                                    <strong>{{ item.guarantor }}</strong>
                                                    <br>
                                                    <small class="text-muted">{{ item.guarantorEmpCode }}</small>
                                                </div>
                                                <span v-else class="text-muted">N/A</span>
                                            </td>
                                            <td>
                                                <span v-if="item.guarantor && item.guarantor !== 'N/A'" 
                                                    :class="`badge bg-${item.isGuarantorAccepted ? 'success' : 'warning'}`">
                                                    {{ item.isGuarantorAccepted ? 'Accepted' : 'Pending' }}
                                                </span>
                                                <span v-else class="text-muted">-</span>
                                            </td>
                                          
                                            <td>
                                                <span :class="`badge bg-${item.autoCheckStatus === 'OK' ? 'success' : 'warning'}`">
                                                    {{ item.autoCheckStatus }}
                                                </span>
                                            </td>
                                            <td>
                                                <span :class="`badge bg-${item.isGuarantorAccepted ? 'danger' : 'secondary'}`">
                                                    {{ item.isGuarantorAccepted ? 'Yes' : 'No' }}
                                                </span>
                                            </td>
                                               <td>
                                                <span :class="`badge bg-${item.status === 'Approved' ? 'success' : 
                                                    item.status === 'Rejected' ? 'danger' : 'warning'}`">
                                                    {{ item.status }}
                                                </span>
                                            </td>
                                             <td>
                                                <button 
                                                    @click="viewPastAdvances(item.pastAdvanceRequests)" 
                                                    class="btn btn-sm btn-primary"
                                                    :disabled="!item.pastAdvanceRequests || item.pastAdvanceRequests.length === 0">
                                                    <i class="fa fa-history me-1"></i>View History
                                                    <span v-if="item.pastAdvanceRequests && item.pastAdvanceRequests.length > 0" 
                                                        class="badge bg-light text-dark ms-1">
                                                        {{ item.pastAdvanceRequests.length }}
                                                    </span>
                                                </button>
                                            </td>
                                         
                                              <td>
                                                <button v-if="item.advanceFormUrl" 
                                                    @click="viewImage(item.advanceFormUrl)" 
                                                    class="btn btn-sm btn-info">
                                                    <i class="fa fa-file-image-o me-1"></i>View
                                                </button>
                                                <span v-else class="text-muted">No Form</span>
                                            </td>
                                           
                                            <td>
                                                <div class="dropdown">
                                                    <button 
                                                        class="btn btn-primary btn-sm dropdown-toggle" 
                                                        type="button" 
                                                        :id="`dropdownMenuButton${item._id}`"
                                                        data-bs-toggle="dropdown" 
                                                        aria-expanded="false"
                                                    >
                                                        Actions
                                                    </button>
                                                    <ul class="dropdown-menu" :aria-labelledby="`dropdownMenuButton${item._id}`">
                                                        <li v-if="item.status !== 'Approved'">
                                                            <a class="dropdown-item" href="#" @click.prevent="handleAction('approve', item)">
                                                                <i class="fa fa-check text-success me-2"></i>Approve
                                                            </a>
                                                        </li>
                                                        <li v-if="item.status !== 'Rejected' && item.status !== 'Approved'">
                                                            <a class="dropdown-item" href="#" @click.prevent="handleAction('reject', item)">
                                                                <i class="fa fa-times text-danger me-2"></i>Reject
                                                            </a>
                                                        </li>
                                                        <li v-if="item.status !== 'Approved'">
                                                            <a class="dropdown-item" href="#" @click.prevent="handleAction('modify', item)">
                                                                <i class="fa fa-edit text-warning me-2"></i>Modify
                                                            </a>
                                                        </li>
                                                        <li><hr class="dropdown-divider"></li>
                                                        <li>
                                                            <a class="dropdown-item text-danger" href="#" @click.prevent="handleAction('delete', item)">
                                                                <i class="fa fa-trash me-2"></i>Delete
                                                            </a>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr v-if="paginatedData.length === 0">
                                            <td colspan="16" class="text-center">No records found.</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div class="d-flex justify-content-between align-items-center mt-3">
                                    <div class="text-muted">
                                        Showing {{ ((currentPage - 1) * elementsPerPage) + 1 }} to 
                                        {{ Math.min(currentPage * elementsPerPage, filteredData.length) }} of 
                                        {{ filteredData.length }} entries
                                    </div>
                                    <ul class="pagination mb-0">
                                        <li class="page-item" :class="{ disabled: currentPage === 1 }" @click="previousPage">
                                            <a class="page-link" href="#" @click.prevent>Previous</a>
                                        </li>
                                        <li 
                                            class="page-item" 
                                            v-for="i in num_pages" 
                                            :key="i" 
                                            :class="{ active: i === currentPage }" 
                                            @click="change_page(i)"
                                        >
                                            <a class="page-link" href="#" @click.prevent>{{ i }}</a>
                                        </li>
                                        <li 
                                            class="page-item" 
                                            :class="{ disabled: currentPage === num_pages }" 
                                            @click="nextPage"
                                        >
                                            <a class="page-link" href="#" @click.prevent>Next</a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <ModalCode v-if="showModal" :show="showModal" :item="selectedItem" @close="showModal = false" @refreshData="getAllData" />
    
    <!-- Image Modal -->
    <div v-if="showImageModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Advance Salary Form</h5>
                    <button type="button" class="btn-close" @click="closeImageModal"></button>
                </div>
                <div class="modal-body text-center">
                    <img :src="selectedImageUrl || ''" alt="Advance Form" class="img-fluid" style="max-height: 70vh; width: auto;">
                </div>
                <div class="modal-footer">
                    <a :href="selectedImageUrl || '#'" target="_blank" class="btn btn-primary">
                        <i class="fa fa-external-link me-2"></i>Open in New Tab
                    </a>
                    <button type="button" class="btn btn-secondary" @click="closeImageModal">Close</button>
                </div>
            </div>
        </div>
    </div>

    <!-- Past Advances Modal -->
    <div v-if="showPastAdvancesModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
        <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header bg-primary text-white">
                    <h5 class="modal-title">
                        <i class="fa fa-history me-2"></i>Past Advance Requests History
                    </h5>
                    <button type="button" class="btn-close btn-close-white" @click="closePastAdvancesModal"></button>
                </div>
                <div class="modal-body">
                    <div v-if="selectedPastAdvances.length === 0" class="text-center py-5 text-muted">
                        <i class="fa fa-inbox fa-3x mb-3"></i>
                        <p>No past advance requests found.</p>
                    </div>
                    <div v-else class="table-responsive">
                        <table class="table table-bordered table-hover">
                            <thead class="table-light">
                                <tr>
                                    <th scope="col" style="width: 50px;">#</th>
                                    <th scope="col">Date</th>
                                    <th scope="col">Amount</th>
                                    <th scope="col">Reason</th>
                                    <th scope="col">Status</th>
                                    <th scope="col">Created At</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(advance, index) in selectedPastAdvances" :key="advance._id">
                                    <td>{{ index + 1 }}</td>
                                    <td>{{ new Date(advance.advanceDate).toLocaleDateString() }}</td>
                                    <td class="fw-bold">₹{{ advance.advanceAmount.toLocaleString() }}</td>
                                    <td>{{ advance.advanceReason }}</td>
                                    <td>
                                        <span :class="`badge bg-${
                                            advance.advanceStatus === 'Approved' ? 'success' : 
                                            advance.advanceStatus === 'Rejected' ? 'danger' : 'warning'
                                        }`">
                                            {{ advance.advanceStatus }}
                                        </span>
                                    </td>
                                    <td>
                                        <small>{{ new Date(advance.createdAt).toLocaleString() }}</small>
                                    </td>
                                
                                </tr>
                            </tbody>
                        </table>
                        <div class="mt-3 p-3 bg-light rounded">
                            <h6 class="mb-2 text-black">Summary:</h6>
                            <div class="row">
                                <div class="col-md-3 text-black">
                                    <strong>Total Requests:</strong> {{ selectedPastAdvances.length }}
                                </div>
                                <div class="col-md-3 text-black">
                                    <strong>Approved:</strong> 
                                    <span class="badge bg-success ms-1">
                                        {{ selectedPastAdvances.filter(a => a.advanceStatus === 'Approved').length }}
                                    </span>
                                </div>
                                <div class="col-md-3 text-black">
                                    <strong>Pending:</strong> 
                                    <span class="badge bg-warning ms-1">
                                        {{ selectedPastAdvances.filter(a => a.advanceStatus === 'Pending').length }}
                                    </span>
                                </div>
                                <div class="col-md-3 text-black">
                                    <strong>Rejected:</strong> 
                                    <span class="badge bg-danger ms-1">
                                        {{ selectedPastAdvances.filter(a => a.advanceStatus === 'Rejected').length }}
                                    </span>
                                </div>
                            </div>
                            <div class="row mt-2">
                                <div class="col-md-6 text-black">
                                    <strong>Total Amount Requested:</strong> 
                                    ₹{{ selectedPastAdvances.reduce((sum, a) => sum + a.advanceAmount, 0).toLocaleString() }}
                                </div>
                                <div class="col-md-6 text-black">
                                    <strong>Total Approved Amount:</strong> 
                                    ₹{{ selectedPastAdvances.filter(a => a.advanceStatus === 'Approved')
                                        .reduce((sum, a) => sum + a.advanceAmount, 0).toLocaleString() }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closePastAdvancesModal">Close</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.modal.show {
    display: block;
}
</style>